import React, { ElementType } from 'react';
import {
  Badge,
  Box,
  Button,
  HStack,
  Icon,
  Input,
  Select,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import {
  CalendarDays,
  CalendarRange,
  Clock3,
  Flag,
  Layers3,
  Link2,
  UserRound,
} from 'lucide-react';
import { CalendarEvent } from '../../../utils/types';

export const calendarAccent = '#0F766E';
export const calendarBorder = 'gray.200';
export const calendarSurface = 'white';

export const formatCalendarDateTime = (value?: string | null) => {
  if (!value) return 'Not set';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not set';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(date);
};

export const formatCalendarDate = (value?: string | null) => {
  if (!value) return 'Not set';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Not set';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};

export const getCalendarEventTypeMeta = (eventType?: string | null) => {
  switch (eventType) {
    case 'task':
      return { label: 'Task', colorScheme: 'purple', tint: '#7C3AED', icon: Layers3 as ElementType };
    case 'lead_followup':
      return { label: 'Lead follow-up', colorScheme: 'orange', tint: '#D97706', icon: Flag as ElementType };
    case 'meeting':
      return { label: 'Meeting', colorScheme: 'green', tint: '#15803D', icon: UserRound as ElementType };
    case 'deadline':
      return { label: 'Deadline', colorScheme: 'red', tint: '#DC2626', icon: Clock3 as ElementType };
    case 'custom':
    default:
      return { label: 'Custom', colorScheme: 'teal', tint: calendarAccent, icon: CalendarDays as ElementType };
  }
};

export const getCalendarStatusMeta = (status?: string | null) => {
  switch (status) {
    case 'completed':
      return { label: 'Completed', colorScheme: 'green' };
    case 'cancelled':
      return { label: 'Cancelled', colorScheme: 'red' };
    case 'upcoming':
    default:
      return { label: 'Upcoming', colorScheme: 'blue' };
  }
};

export const CalendarShell = ({
  compact = false,
  children,
}: {
  compact?: boolean;
  children: React.ReactNode;
}) => (
  <Box bg="#F6F8FB" minH="100vh" px={{ base: 4, md: 6, xl: 8 }} py={{ base: 4, md: 6 }}>
    <Box maxW={compact ? '1120px' : '1360px'} mx="auto">
      {children}
    </Box>
  </Box>
);

export const CalendarHero = ({
  eyebrow,
  title,
  description,
  primaryAction,
  secondaryAction,
}: {
  eyebrow: string;
  title: string;
  description: string;
  primaryAction?: React.ReactNode;
  secondaryAction?: React.ReactNode;
}) => (
  <Box
    bg="white"
    borderWidth="1px"
    borderColor={calendarBorder}
    borderRadius={{ base: '22px', md: '26px' }}
    px={{ base: 5, md: 7 }}
    py={{ base: 5, md: 6 }}
    boxShadow="0 20px 44px rgba(15, 23, 42, 0.05)"
    mb={6}
  >
    <Stack direction={{ base: 'column', xl: 'row' }} justify="space-between" align={{ base: 'flex-start', xl: 'center' }} spacing={4}>
      <Box maxW="760px">
        <Text fontSize="xs" textTransform="uppercase" letterSpacing="0.14em" fontWeight="800" color="gray.500" mb={2}>
          {eyebrow}
        </Text>
        <Text fontSize={{ base: '3xl', md: '4xl' }} lineHeight={{ base: '1.1', md: '1.05' }} fontWeight="800" color="gray.900">
          {title}
        </Text>
        <Text mt={3} color="gray.600" maxW="620px" lineHeight="1.65">
          {description}
        </Text>
      </Box>
      <Stack direction={{ base: 'column', sm: 'row' }} spacing={3} w={{ base: '100%', xl: 'auto' }}>
        {secondaryAction}
        {primaryAction}
      </Stack>
    </Stack>
  </Box>
);

export const CalendarPrimaryButton = ({
  children,
  leftIcon,
  onClick,
  isLoading,
}: {
  children: React.ReactNode;
  leftIcon?: React.ReactElement;
  onClick?: () => void;
  isLoading?: boolean;
}) => (
  <Button
    onClick={onClick}
    leftIcon={leftIcon}
    isLoading={isLoading}
    bg={calendarAccent}
    color="white"
    borderRadius="16px"
    px={5}
    _hover={{ bg: '#115E59' }}
    _active={{ bg: '#134E4A' }}
    w={{ base: '100%', sm: 'auto' }}
  >
    {children}
  </Button>
);

export const CalendarSecondaryButton = ({
  children,
  leftIcon,
  onClick,
}: {
  children: React.ReactNode;
  leftIcon?: React.ReactElement;
  onClick?: () => void;
}) => (
  <Button
    variant="outline"
    leftIcon={leftIcon}
    onClick={onClick}
    borderRadius="16px"
    borderColor="gray.300"
    color="gray.700"
    w={{ base: '100%', sm: 'auto' }}
  >
    {children}
  </Button>
);

export const CalendarSectionCard = ({
  title,
  description,
  action,
  children,
}: {
  title: string;
  description?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) => (
  <Box
    bg={calendarSurface}
    borderWidth="1px"
    borderColor={calendarBorder}
    borderRadius={{ base: '20px', md: '24px' }}
    px={{ base: 4, md: 6 }}
    py={{ base: 4, md: 5 }}
    boxShadow="0 18px 40px rgba(15, 23, 42, 0.05)"
  >
    <Stack direction={{ base: 'column', md: 'row' }} justify="space-between" align={{ base: 'flex-start', md: 'center' }} mb={5} spacing={3}>
      <Box>
        <Text fontSize={{ base: 'lg', md: 'xl' }} fontWeight="800" color="gray.900">
          {title}
        </Text>
        {description ? (
          <Text fontSize="sm" color="gray.500" mt={1}>
            {description}
          </Text>
        ) : null}
      </Box>
      {action}
    </Stack>
    {children}
  </Box>
);

export const CalendarTypeBadge = ({ type }: { type?: string | null }) => {
  const meta = getCalendarEventTypeMeta(type);
  return (
    <Badge colorScheme={meta.colorScheme} borderRadius="full" px={3} py={1.5}>
      {meta.label}
    </Badge>
  );
};

export const CalendarStatusBadge = ({ status }: { status?: string | null }) => {
  const meta = getCalendarStatusMeta(status);
  return (
    <Badge colorScheme={meta.colorScheme} borderRadius="full" px={3} py={1.5}>
      {meta.label}
    </Badge>
  );
};

export const CalendarField = ({ label, value }: { label: string; value: string }) => (
  <Box>
    <Text fontSize="xs" textTransform="uppercase" letterSpacing="0.12em" fontWeight="800" color="gray.500" mb={1.5}>
      {label}
    </Text>
    <Text color="gray.900" fontWeight="600">
      {value}
    </Text>
  </Box>
);

export const CalendarCommandBar = ({
  search,
  onSearchChange,
  type,
  onTypeChange,
  status,
  onStatusChange,
  assignee,
  onAssigneeChange,
  assignees,
  scope,
  onScopeChange,
  view,
  onViewChange,
}: {
  search: string;
  onSearchChange: (value: string) => void;
  type: string;
  onTypeChange: (value: string) => void;
  status: string;
  onStatusChange: (value: string) => void;
  assignee: string;
  onAssigneeChange: (value: string) => void;
  assignees: Array<{ id: string; label: string }>;
  scope: string;
  onScopeChange: (value: string) => void;
  view: string;
  onViewChange: (value: string) => void;
}) => (
  <Box
    bg="white"
    borderWidth="1px"
    borderColor="gray.100"
    borderRadius={{ base: '18px', md: '20px' }}
    px={{ base: 3, md: 4 }}
    py={{ base: 3, md: 3.5 }}
    boxShadow="0 8px 24px rgba(15, 23, 42, 0.04)"
  >
    <Stack direction={{ base: 'column', xl: 'row' }} spacing={2.5} align={{ base: 'stretch', xl: 'center' }}>
      <Input
        value={search}
        onChange={(event) => onSearchChange(event.target.value)}
        placeholder="Search schedule"
        size="sm"
        borderRadius="12px"
        bg="white"
        borderColor="gray.100"
        _focusVisible={{ borderColor: 'teal.400', boxShadow: '0 0 0 1px rgba(15, 118, 110, 0.28)' }}
        flex={1}
      />
      <Stack direction={{ base: 'column', sm: 'row' }} spacing={2.5} minW={{ xl: '760px' }} flexWrap="wrap">
        <Select
          value={scope}
          onChange={(event) => onScopeChange(event.target.value)}
          size="sm"
          borderRadius="12px"
          bg="white"
          borderColor="gray.100"
        >
          <option value="all">All ownership</option>
          <option value="mine">My events</option>
          <option value="unassigned">Unassigned</option>
        </Select>
        <Select
          value={assignee}
          onChange={(event) => onAssigneeChange(event.target.value)}
          size="sm"
          borderRadius="12px"
          bg="white"
          borderColor="gray.100"
        >
          <option value="all">All assignees</option>
          {assignees.map((item) => (
            <option key={item.id} value={item.id}>
              {item.label}
            </option>
          ))}
        </Select>
        <Select
          value={view}
          onChange={(event) => onViewChange(event.target.value)}
          size="sm"
          borderRadius="12px"
          bg="white"
          borderColor="gray.100"
          maxW={{ xl: '120px' }}
        >
          <option value="month">Month</option>
          <option value="week">Week</option>
          <option value="day">Day</option>
          <option value="agenda">Agenda</option>
        </Select>
        <Select
          value={type}
          onChange={(event) => onTypeChange(event.target.value)}
          size="sm"
          borderRadius="12px"
          bg="white"
          borderColor="gray.100"
        >
          <option value="all">All event types</option>
          <option value="task">Tasks</option>
          <option value="lead_followup">Lead follow-ups</option>
          <option value="meeting">Meetings</option>
          <option value="deadline">Deadlines</option>
          <option value="custom">Custom</option>
        </Select>
        <Select
          value={status}
          onChange={(event) => onStatusChange(event.target.value)}
          size="sm"
          borderRadius="12px"
          bg="white"
          borderColor="gray.100"
          maxW={{ xl: '150px' }}
        >
          <option value="all">All statuses</option>
          <option value="upcoming">Upcoming</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </Select>
      </Stack>
    </Stack>
  </Box>
);

export const CalendarEventListItem = ({
  event,
  onClick,
}: {
  event: CalendarEvent;
  onClick: () => void;
}) => {
  const meta = getCalendarEventTypeMeta(event.event_type);
  return (
    <Button
      variant="ghost"
      justifyContent="flex-start"
      h="auto"
      px={0}
      py={0}
      onClick={onClick}
      _hover={{ bg: 'transparent' }}
      _active={{ bg: 'transparent' }}
      w="100%"
    >
      <HStack
        align="stretch"
        spacing={3}
        w="100%"
        bg="gray.50"
        borderWidth="1px"
        borderColor="gray.200"
        borderRadius="18px"
        px={3.5}
        py={3}
      >
        <Box w="4px" borderRadius="full" bg={meta.tint} />
        <VStack align="flex-start" spacing={1} flex={1}>
          <Text fontWeight="700" color="gray.900" noOfLines={1}>
            {event.title}
          </Text>
          <HStack spacing={2} flexWrap="wrap">
            <CalendarTypeBadge type={event.event_type} />
            <CalendarStatusBadge status={event.status} />
          </HStack>
          <Text fontSize="sm" color="gray.500">
            {formatCalendarDateTime(event.start_datetime)}
          </Text>
          <HStack spacing={3} flexWrap="wrap">
            <HStack spacing={1.5} color="gray.500">
              <Icon as={UserRound as ElementType} boxSize={3.5} />
              <Text fontSize="xs">{event.assigned_to_name || 'Unassigned'}</Text>
            </HStack>
            <HStack spacing={1.5} color="gray.500">
              <Icon as={Link2 as ElementType} boxSize={3.5} />
              <Text fontSize="xs">
                {event.related_source === 'task'
                  ? 'Task'
                  : event.related_source === 'lead_followup'
                    ? 'Lead follow-up'
                    : 'Standalone'}
              </Text>
            </HStack>
          </HStack>
        </VStack>
      </HStack>
    </Button>
  );
};

export const CalendarEmptyState = ({
  title,
  description,
  action,
}: {
  title: string;
  description: string;
  action?: React.ReactNode;
}) => (
  <VStack py={16} px={6} spacing={3} textAlign="center">
    <Box boxSize="56px" borderRadius="full" bg="teal.50" display="flex" alignItems="center" justifyContent="center" color={calendarAccent}>
      <Icon as={CalendarRange as ElementType} boxSize={7} />
    </Box>
    <Text fontWeight="800" color="gray.900">
      {title}
    </Text>
    <Text fontSize="sm" color="gray.500" maxW="360px">
      {description}
    </Text>
    {action}
  </VStack>
);

export const CalendarSkeleton = () => (
  <Stack spacing={6}>
    <Box bg="white" borderWidth="1px" borderColor={calendarBorder} borderRadius="24px" p={6}>
      <Skeleton h="18px" w="120px" mb={4} />
      <Skeleton h="34px" w={{ base: '74%', md: '52%' }} mb={3} />
      <Skeleton h="16px" w={{ base: '88%', md: '60%' }} />
    </Box>
    <Box bg="white" borderWidth="1px" borderColor={calendarBorder} borderRadius="24px" p={6}>
      <Skeleton h="48px" mb={5} borderRadius="18px" />
      <SimpleGrid columns={{ base: 1, xl: 3 }} spacing={6}>
        <Box gridColumn={{ xl: 'span 2' }}>
          <Skeleton h="620px" borderRadius="20px" />
        </Box>
        <Stack spacing={4}>
          {Array.from({ length: 4 }).map((_, index) => (
            <Skeleton key={index} h="92px" borderRadius="18px" />
          ))}
        </Stack>
      </SimpleGrid>
    </Box>
  </Stack>
);
